import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Crown, ArrowLeft } from 'lucide-react';
import { useActivePlans } from '../../hooks/useSubscription';
import { useCreatePaymentOrder } from '../../hooks/usePayment';
import useAuthStore from '../../store/authStore';
import { toast } from 'react-toastify';
import styles from './SubscriptionPlans.module.css';

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { plans, isLoading: plansLoading } = useActivePlans();
  const createPaymentOrder = useCreatePaymentOrder();
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleSubscribe = async (planId) => {
    // Prevent multiple clicks
    if (createPaymentOrder.isLoading) {
      return;
    }

    try {
      const response = await createPaymentOrder.mutateAsync(planId);
      
      console.log('Payment order response:', response);
      
      if (response.order && response.order.payment_url) {
        // Redirect to Cashfree payment page
        window.location.href = response.order.payment_url;
      } else if (response.order && response.order.payment_session_id) {
        // If we have session ID but no URL, construct the payment URL
        const paymentUrl = `https://api.cashfree.com/pg/view/sessions/checkout/web/${response.order.payment_session_id}`;
        window.location.href = paymentUrl;
      } else {
        toast.error('Payment URL not received. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment initiation failed. Please try again.');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const formatPrice = (price, billingCycle) => {
    const formattedPrice = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);

    return `${formattedPrice}/${billingCycle}`;
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.popup}>
        <div className={styles.headerContainer}>
          <button 
            onClick={handleBack}
            className={styles.backButton}
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <h1 className={styles.pageTitle}>Choose Your Plan</h1>
          <div className={styles.spacer}></div>
        </div>

        <div className={styles.header}>
          <p className={styles.subtitle}>
            Get unlimited access to all audio books, stories, and premium features
          </p>
        </div>

        {plansLoading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading plans...</p>
          </div>
        ) : (
          <div className={styles.plansContainer}>
            {plans?.plans?.map((plan) => (
              <div
                key={plan.id}
                className={`${styles.planCard} ${selectedPlan?.id === plan.id ? styles.selected : ''} ${plan.popular ? styles.popular : ''}`}
                onClick={() => setSelectedPlan(plan)}
              >
                <div className={styles.planHeader}>
                  {plan.popular && (
                    <div className={styles.popularBadge}>Most Popular</div>
                  )}
                  <h3 className={styles.planName}>{plan.name}</h3>
                  <div className={styles.priceContainer}>
                    <span className={styles.price}>
                      {formatPrice(plan.price, plan.billing_cycle)}
                    </span>
                  </div>
                </div>

                {plan.description && (
                  <p className={styles.planDescription}>{plan.description}</p>
                )}

                <div className={styles.features}>
                  <div className={styles.feature}>
                    <Check className={styles.checkIcon} />
                    <span>Unlimited audio book access</span>
                  </div>
                  <div className={styles.feature}>
                    <Check className={styles.checkIcon} />
                    <span>Ad-free listening experience</span>
                  </div>
                  <div className={styles.feature}>
                    <Check className={styles.checkIcon} />
                    <span>Premium support</span>
                  </div>
                  <div className={styles.feature}>
                    <Check className={styles.checkIcon} />
                    <span>Early access to new content</span>
                  </div>
                  <div className={styles.feature}>
                    <Check className={styles.checkIcon} />
                    <span>Offline downloads</span>
                  </div>
                </div>

                <button
                  className={styles.subscribeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubscribe(plan.id);
                  }}
                  disabled={createPaymentOrder.isLoading}
                >
                  {createPaymentOrder.isLoading ? 'Processing...' : 'Subscribe Now'}
                </button>
              </div>
            ))}
          </div>
        )}

        <div className={styles.footer}>
          <p className={styles.footerText}>
            Cancel anytime. No commitment required.
          </p>
          <button 
            className={styles.skipButton} 
            onClick={handleBack}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans; 